import { AutoPostSlot } from 'services/post/state'
import { PlannedPost } from 'shared/types'
import { sortAscending } from 'shared/utils'

type ReorderResult = {
  posts: PlannedPost[]
  ids: string[]
}

export function getReorderedPosts(
  movedPost: PlannedPost,
  targetPost: PlannedPost,
  posts: PlannedPost[],
  autoPostSlots: AutoPostSlot[]
): ReorderResult | null {
  const { ppPageId: ppid, bucketId } = movedPost

  const autoPostsByProfile = posts
    .filter(p => p.autoPost && !p.isEmpty && !p.isVirtual && !p.isPosted && p.ppPageId === ppid && p.bucketId === bucketId)
    .sort(sortAscending('autoPostOrder')) as PlannedPost[]

  let currentIndex = -1
  let nextIndex = -1
  for (let i = 0; i < autoPostsByProfile.length; i++) {
    const pid = autoPostsByProfile[i].id
    if (pid === movedPost.id) currentIndex = i
    if (pid === targetPost.id) nextIndex = i
    if (currentIndex !== -1 && nextIndex !== -1) break
  }

  if (currentIndex === -1 || nextIndex === -1 || currentIndex === nextIndex) {
    return null
  }

  autoPostsByProfile.splice(currentIndex, 1)
  autoPostsByProfile.splice(nextIndex, 0, movedPost)

  const slots = autoPostSlots.filter(s => s.ppPageId === ppid && s.bucketId === bucketId)
  const updatedMap = new Map<string, PlannedPost>()
  const ids: string[] = []

  for (let i = 0; i < autoPostsByProfile.length; i++) {
    const post = autoPostsByProfile[i]
    const slot = slots[i]
    ids.push(post.id)
    if (slot) {
      updatedMap.set(post.id, {
        ...post,
        time: slot.time,
        dateString: slot.dateString,
        weekday: slot.weekday,
        autoPostOrder: i,
        timeString: slot.timeString,
        hoursLocal: slot.hoursLocal
      })
    }
  }

  const updatedPosts = posts.map(post => updatedMap.get(post.id) || post)
  updatedPosts.sort(sortAscending('time'))

  return { posts: updatedPosts, ids }
}

type MoveResult = {
  posts: PlannedPost[]
  ids: string[]
  moveTo?: 'top' | 'bottom'
}

export function getMovedPostResult(
  movedPost: PlannedPost,
  position: 'top' | 'bottom' | 'up' | 'down',
  posts: PlannedPost[],
  autoPostSlots: AutoPostSlot[]
): MoveResult | null {
  const { id, ppPageId: ppid, bucketId } = movedPost

  const autoPostsByProfile = posts
    .filter(p => p.autoPost && !p.isEmpty && !p.isVirtual && !p.isPosted && p.bucketId === bucketId && p.ppPageId === ppid)
    .sort(sortAscending('autoPostOrder')) as PlannedPost[]

  const ids = autoPostsByProfile.map(p => p.id)
  const currentIndex = ids.indexOf(id)
  if (currentIndex === -1) return null

  ids.splice(currentIndex, 1)

  switch (position) {
    case 'top':
      ids.unshift(id)
      break
    case 'bottom':
      ids.push(id)
      break
    case 'up':
      if (currentIndex > 0) {
        ids.splice(currentIndex - 1, 0, id)
      } else {
        return null
      }
      break
    case 'down':
      if (currentIndex < ids.length) {
        ids.splice(currentIndex + 1, 0, id)
      } else {
        return null
      }
      break
    default:
      return null
  }

  const postMap = new Map(autoPostsByProfile.map(p => [p.id, p]))
  const reorderedPosts = ids.map(pid => postMap.get(pid)!)

  const slots = autoPostSlots.filter(s => s.ppPageId === ppid && s.bucketId === bucketId)
  const updatedMap = new Map<string, PlannedPost>()

  for (let i = 0; i < reorderedPosts.length; i++) {
    const post = reorderedPosts[i]
    const slot = slots[i]
    if (slot) {
      updatedMap.set(post.id, {
        ...post,
        time: slot.time,
        dateString: slot.dateString,
        weekday: slot.weekday,
        autoPostOrder: i,
        timeString: slot.timeString,
        hoursLocal: slot.hoursLocal
      })
    }
  }

  const updatedPosts = posts.map(post => updatedMap.get(post.id) || post)
  updatedPosts.sort(sortAscending('time'))

  const moveTo = position === 'top' || position === 'bottom' ? position : undefined

  return { posts: updatedPosts, ids, moveTo }
}

export function getBulkMovePostsResult(
  movedPosts: PlannedPost[],
  position: 'top' | 'bottom',
  posts: PlannedPost[],
  autoPostSlots: AutoPostSlot[]
): MoveResult | null {
  if (movedPosts.length === 0) return null

  // Group moved posts by ppPageId + bucketId combination
  const movedPostsByQueue = new Map<string, PlannedPost[]>()
  movedPosts.forEach(post => {
    const queueKey = `${post.ppPageId}_${post.bucketId ?? 'null'}`
    if (!movedPostsByQueue.has(queueKey)) {
      movedPostsByQueue.set(queueKey, [])
    }
    movedPostsByQueue.get(queueKey)!.push(post)
  })

  const updatedMap = new Map<string, PlannedPost>()
  const allReorderedIds: string[] = []

  // Process each queue separately
  movedPostsByQueue.forEach((queueMovedPosts, queueKey) => {
    const [ppPageId, bucketIdStr] = queueKey.split('_')
    const bucketId = bucketIdStr === 'null' ? undefined : bucketIdStr

    // Get auto posts for this specific queue
    const autoPostsByProfile = posts
      .filter(p => p.autoPost && !p.isEmpty && !p.isVirtual && !p.isPosted && p.bucketId === bucketId && p.ppPageId === ppPageId)
      .sort(sortAscending('autoPostOrder')) as PlannedPost[]

    const ids = autoPostsByProfile.map(p => p.id)
    const movedPostIds = queueMovedPosts.map(p => p.id)

    // Remove all moved posts from their current positions in this queue
    const filteredIds = ids.filter(id => !movedPostIds.includes(id))

    // Add moved posts to the specified position in this queue
    switch (position) {
      case 'top':
        filteredIds.unshift(...movedPostIds)
        break
      case 'bottom':
        filteredIds.push(...movedPostIds)
        break
      default:
        return
    }

    // Get slots for this specific queue
    const slots = autoPostSlots.filter(s => s.ppPageId === ppPageId && s.bucketId === bucketId)
    const postMap = new Map(autoPostsByProfile.map(p => [p.id, p]))
    const reorderedPosts = filteredIds.map(pid => postMap.get(pid)!)

    // Update posts with new slot assignments for this queue
    for (let i = 0; i < reorderedPosts.length; i++) {
      const post = reorderedPosts[i]
      const slot = slots[i]
      if (slot) {
        updatedMap.set(post.id, {
          ...post,
          time: slot.time,
          dateString: slot.dateString,
          weekday: slot.weekday,
          autoPostOrder: i,
          timeString: slot.timeString,
          hoursLocal: slot.hoursLocal
        })
      }
    }

    allReorderedIds.push(...filteredIds)
  })

  // Merge all queues by updating the original posts array and sorting by time
  const updatedPosts = posts.map(post => updatedMap.get(post.id) || post)
  updatedPosts.sort(sortAscending('time'))

  return { posts: updatedPosts, ids: allReorderedIds, moveTo: position }
}
